// src\modules\Products\CreateEdit\components\modals\CreateStockModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Input } from '@shared/components/ui/input';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';

export interface CreateStockModalRef {
  open: (
    onConfirm: (data: any) => void,
    options?: {
      isEdit?: boolean;
      locationId?: string | number;
      locationCode?: string;
    },
  ) => void;
}

interface CreateStockModalProps {
  ubicaciones: { value: string; label: string; typeName?: string }[];
  loadingUbicaciones: boolean;
  filtrarUbicaciones: (searchText: string) => { value: string; label: string; typeName?: string }[];
  stocks: any[];
  productId?: string | number;
}

export const CreateStockModal = forwardRef<CreateStockModalRef, CreateStockModalProps>(
  ({ ubicaciones, loadingUbicaciones, productId }, ref) => {
    const { t } = useTranslation('createEditProductsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const confirmRef = useRef<(data: any) => void>(() => {});
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [ubicacion, setUbicacion] = useState('');
    const [unidades, setUnidades] = useState(0);
    const [motivo, setMotivo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [locationCode, setLocationCode] = useState('');

    useImperativeHandle(ref, () => ({
      open: (onConfirm, options) => {
        confirmRef.current = onConfirm;
        const editMode = Boolean(options?.isEdit);
        setIsEdit(editMode);
        setUbicacion(options?.locationId ? String(options.locationId) : '');
        setLocationCode(options?.locationCode || '');
        setUnidades(0);
        setMotivo('');
        modalRef.current?.openModal();
      },
    }));

    const handleConfirm = async () => {
      if (!productId) {
        toast({
          title: t('form.error_title'),
          description: t('form.error_desc'),
          variant: 'destructive',
        });
        return;
      }
      if (!ubicacion || unidades <= 0) {
        toast({
          title: t('form.error_title'),
          description: t('form.error_required'),
          variant: 'destructive',
        });
        return;
      }

      setSubmitting(true);
      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ProductsApiAdapter(httpClient);
        await adapter.adjustStock({
          locationId: ubicacion,
          productId: productId,
          quantity: unidades,
          description: motivo,
        });

        toast({
          title: t('form.stock_adjust_success_title'),
          description: t('sections.stock_adjust'),
          variant: 'success',
        });

        // Refrescar tabla de ubicaciones con stock
        await queryClient.invalidateQueries({
          queryKey: ['locations-by-product', productId],
        });

        confirmRef.current({ ubicacion, unidades, motivo });
        modalRef.current?.closeModal();
      } catch (error: any) {
        const data = error?.response?.data;
        const apiMessage =
          data?.message ||
          data?.error?.message ||
          (Array.isArray(data?.errors) && data.errors[0]?.message) ||
          data?.error_description ||
          error?.message;
        toast({
          title: t('form.stock_adjust_error_title'),
          description: apiMessage || t('form.error_desc'),
          variant: 'destructive',
        });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <ModalCenter ref={modalRef} title={t('modal.add_stock_title')} type="default" width="lg" fullScreenMobile={true}>
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto space-y-6 mt-1">
            <div className="grid grid-cols-1 md:grid-cols-10 gap-4 items-end">
              <div className="md:col-span-7">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-text">{t('modal.ubicacion_label')}</label>
                  <InfoTooltip
                    text={t('modal.ubicacion_info')}
                    iconClassName="w-3.5 h-3.5"
                    dataTestId="ubicacion-tooltip"
                  />
                </div>
                {isEdit ? (
                  <div
                    className="h-10 flex items-center px-3 rounded border border-lines bg-card-bg text-text"
                    data-testid="stock-location-readonly"
                  >
                    {locationCode || ubicacion}
                  </div>
                ) : (
                  <Combobox
                    options={ubicaciones}
                    placeholder={loadingUbicaciones ? 'Cargando ubicaciones...' : t('modal.ubicacion_placeholder')}
                    value={ubicacion}
                    setValue={setUbicacion}
                    dataTestId="combobox-ubicacion"
                    data-testid="stock-location-combobox"
                    disabled={loadingUbicaciones}
                  />
                )}
              </div>
              <div className="md:col-span-3 max-w-[150px]">
                <label className="block text-sm mb-2 text-text">{t('modal.unidades_label')}</label>
                <StepperInput
                  value={unidades}
                  onChange={setUnidades}
                  min={0}
                  max={99999}
                  data-testid="stock-units-stepper"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm mb-2 text-text">{t('modal.motivo_label')}</label>
              <Input
                placeholder={t('modal.motivo_placeholder')}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                data-testid="stock-reason-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="cancel-stock-modal-button"
            >
              {t('modal.cancelar')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleConfirm}
              disabled={!ubicacion || unidades <= 0 || submitting}
              data-testid="confirm-stock-modal-button"
            >
              {t('modal.anadir')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateStockModal.displayName = 'CreateStockModal';
